// Сегментированный переключатель 2–5 значений (components/Segmented/README.md):
// role="tablist", у выбранного aria-selected; стрелки, Home и End переключают сразу, без тоста.
import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import type { LucideIcon } from '../lib/icon.tsx';

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
  /** Счётчик серым внутри сегмента. */
  count?: number;
  icon?: LucideIcon;
}

export interface SegmentedProps<V extends string> {
  /** Подпись группы для программ чтения экрана. */
  label: string;
  value: V;
  onChange: (value: V) => void;
  options: readonly SegmentedOption<V>[];
  className?: string;
}

export function Segmented<V extends string>({
  label,
  value,
  onChange,
  options,
  className,
}: SegmentedProps<V>) {
  const listRef = useRef<HTMLDivElement>(null);

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = options.findIndex((option) => option.value === value);
    let next: number;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + options.length) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={cx('t-seg', className)}
      onKeyDown={move}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={cx(selected && 'on')}
            onClick={() => onChange(option.value)}
          >
            {option.icon ? <Icon icon={option.icon} /> : null}
            {option.label}
            {option.count !== undefined ? (
              <span className="t-cnt t-num">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
